/* Small shared pieces, and the one piece of visual judgement in the app:
   what colour a maturity score is. */
import { useEffect, useRef, useState, type ReactNode } from "react";

import { GENERIC_SCALE, SCORING, bandFor } from "../scoring";

/* Colour follows the methodology's anchors, not a continuous ramp: grey, red,
   orange, yellow, light green, dark green at 0 · 10 · 30 · 60 · 80 · 100. A
   score of 40 is "past 30, short of 60", so it is coloured as 30 — the colour
   states which rung has actually been cleared rather than how close the number
   looks to the next one. See `scoring.ts`.

   Not assessed is not a colour. It is blank, because a fill on the same scale
   as the numbers reads as a low score, and "nobody has looked at this yet" is a
   different statement from "this is at zero" -- which has its own colour, grey.

   The values are CSS variables, so light and dark mode stay the theme's
   problem. */
export function scoreStyle(value: number | null): React.CSSProperties {
  const band = bandFor(value);
  if (band === null) return {};
  return { background: `var(--m${band})` };
}

export function Score({ value }: { value: number | null }) {
  if (value === null || value === undefined) return <span className="na">—</span>;
  return <>{Math.round(value)}</>;
}

export function Bar({ value }: { value: number | null }) {
  const width = value === null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="bar">
      <i style={{ width: `${width}%`, ...scoreStyle(value), opacity: 1 }} />
    </div>
  );
}

export function Chip({ text, tone }: { text: string; tone?: "good" | "warn" | "bad" | "" }) {
  return <span className={`chip ${tone ?? ""}`}>{text}</span>;
}

export function priorityTone(priority: string): "bad" | "warn" | "" {
  if (priority === "High") return "bad";
  if (priority === "Medium") return "warn";
  return "";
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
    </div>
  );
}

export function Panel({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="panel">
      <div className="panel-hd">
        <h2>{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/* Dates arrive as yyyy-mm-dd and are compared as strings, which is correct for
   that format and avoids a timezone shifting a milestone across midnight. */
export function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function DueDate({ date, status }: { date: string | null; status?: string }) {
  if (!date) return <span className="muted">no date</span>;
  const overdue = date < today() && status !== "done";
  return <span className={overdue ? "overdue" : undefined}>{date}{overdue ? " · overdue" : ""}</span>;
}

/* ── maturity dimension icons ──────────────────────────────────────────────

   The heatmap has seven score columns plus five of context, and spelling the
   dimension names out pushed the table past the width of the container: a
   horizontal scrollbar under a heatmap is the worst of both, because the row
   you are reading and the column you are reading cannot both be on screen.

   Icons cost recognition, which is why they are not left to carry it alone --
   each header has a hover hint that names the dimension and says what a high
   score in it would mean, and the same icon and hint appear beside the named
   field on the object's own page, which is where the number is set and where
   the seven get learned. */
const HINTS: Record<string, string> = {
  strategy: "Is the offering defined — who it is for, what it promises, why us?",
  core_capabilities: "Do we have the people, tooling and track record to deliver it?",
  delivery_readiness: "Could a team start on Monday — playbook, estimates, staffing?",
  marketing_assets: "Website pages and collateral, published and on-message.",
  sales_enablement: "Can Sales pitch it — pricing, objections, qualifying questions?",
  market_validation: "Has the market said yes, ideally by paying for it?",
  presales_training: "Have Presales and client-facing Delivery been trained on it?",
};

/* The three dimensions assets share with services ask the same underlying
   question, but the methodology words them for something that is sold. An
   asset is not: a methodology reference has no buyer, no promise and no
   Monday-morning engagement. So assets get their own wording for the same
   three rungs -- is it defined, can we do it, has it left the author's head. */
const ASSET_HINTS: Record<string, string> = {
  strategy: "Is it defined — what it is for, who relies on it, and what it has to cover?",
  core_capabilities: "Do the people, tooling and track record behind it actually exist?",
  delivery_readiness:
    "Could someone else pick it up, or does it live in one person's head?",
};

export const hintFor = (key: string, label: string, kind: "service" | "asset" = "service") => {
  const hint = (kind === "asset" ? ASSET_HINTS[key] : undefined) ?? HINTS[key] ?? "";
  return `${label} — ${hint}`.trim();
};

export function DimensionIcon({ dimension }: { dimension: string }) {
  const shape = () => {
    switch (dimension) {
      case "strategy": // a target: the thing aimed at
        return (
          <>
            <circle cx="8" cy="8" r="6" />
            <circle cx="8" cy="8" r="2.3" />
            <path d="M8 0.9v2.2M8 12.9v2.2M0.9 8h2.2M12.9 8h2.2" />
          </>
        );
      case "core_capabilities": // a solid: what the offering is built out of
        return (
          <>
            <path d="M8 1.7 13.9 5v6L8 14.3 2.1 11V5z" />
            <path d="M2.1 5 8 8.3 13.9 5M8 8.3v6" />
          </>
        );
      case "delivery_readiness": // a truck: can it actually be shipped
        return (
          <>
            <path d="M1.7 4.6h7.1v6.2H1.7z" />
            <path d="M8.8 6.9h2.6l2 2.3v1.6H8.8z" />
            <circle cx="4.5" cy="12.1" r="1.3" />
            <circle cx="11.3" cy="12.1" r="1.3" />
          </>
        );
      case "marketing_assets": // a megaphone: what is said in public
        return (
          <>
            <path d="M2.2 6.3v3.4h2.5l4.7 2.9V3.4L4.7 6.3z" />
            <path d="M11.8 5.6a3.5 3.5 0 0 1 0 4.8" />
          </>
        );
      case "sales_enablement": // a briefcase: what a seller carries in
        return (
          <>
            <path d="M1.9 5.4h12.2v7.4H1.9z" />
            <path d="M5.9 5.4V3.9a1 1 0 0 1 1-1h2.2a1 1 0 0 1 1 1v1.5" />
            <path d="M1.9 8.7h12.2" />
          </>
        );
      case "market_validation": // a shield with a tick: proof it holds up
        return (
          <>
            <path d="M8 1.7 13.5 3.9v4.2c0 3-2.3 5.2-5.5 6.2-3.2-1-5.5-3.2-5.5-6.2V3.9z" />
            <path d="m5.7 8 1.7 1.7 3.1-3.3" />
          </>
        );
      case "presales_training": // a mortarboard: who has been taught it
        return (
          <>
            <path d="M8 2.8 1.6 6 8 9.2 14.4 6z" />
            <path d="M4.3 7.5v3.2c0 1 1.7 1.8 3.7 1.8s3.7-.8 3.7-1.8V7.5" />
          </>
        );
      default:
        return <circle cx="8" cy="8" r="5.5" />;
    }
  };
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {shape()}
    </svg>
  );
}

/* ── multi-select filter ───────────────────────────────────────────────────

   A native <select multiple> is the cheap answer and the wrong one: it needs
   ctrl-click to add a value, which nobody discovers, and it either takes six
   rows of vertical space or hides its own selection behind a scroll.

   So: a button that states what is selected, and a popover of checkboxes. The
   button always reads "3 of 6" rather than the list, because the list is what
   makes a filter bar grow every time somebody picks something. */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const all = selected.length === options.length;
  const summary = all ? "All" : selected.length === 0 ? "None" : `${selected.length} of ${options.length}`;

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  return (
    <div className="ms" ref={box}>
      <label>{label}</label>
      <button
        type="button"
        className={`msbtn${all ? "" : " narrowed"}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {summary}
        <span className="caret">▾</span>
      </button>
      {open && (
        <div className="pop">
          <div className="poprow">
            <button className="linkbtn" onClick={() => onChange([...options])}>
              all
            </button>
            <button className="linkbtn" onClick={() => onChange([])}>
              none
            </button>
          </div>
          {options.map((option) => (
            <label key={option} className="popitem">
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggle(option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* A panel that starts closed. Built on <details> rather than on a boolean and
   a conditional render: keyboard support, and the open state survives a
   re-render without being lifted into React state that then has to be kept. */
export function CollapsiblePanel({
  title,
  aside,
  children,
  open = false,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  open?: boolean;
}) {
  return (
    <details className="panel collapsible" open={open}>
      <summary>
        <h2>{title}</h2>
        <span className="sum-aside">{aside}</span>
      </summary>
      <div className="collapsible-body">{children}</div>
    </details>
  );
}

/* ── the scale, on demand ──────────────────────────────────────────────────

   Clicking a dimension's name opens its scale. Everything in here is
   transcribed from `docs/maturity-scoring-methodology.md`: the question the
   dimension asks, where its boundary with the neighbouring dimension runs, its
   components, and the six anchors with the test for each.

   A dialog rather than prose on the page. Somebody setting a score needs the
   anchor tests in front of them at that moment; everybody else, on every other
   visit, does not. */
export function ScaleDialog({
  dimension,
  label,
  onClose,
}: {
  dimension: string | null;
  label: string;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!dimension) return;
    const escape = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [dimension, onClose]);

  if (!dimension) return null;
  const doc = SCORING[dimension];
  if (!doc) return null;

  return (
    <div className="scrim" onClick={onClose} role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`${label} scoring scale`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dialog-hd">
          <h2>
            <DimensionIcon dimension={dimension} />
            {label}
          </h2>
          <button className="linkbtn" onClick={onClose} aria-label="Close">
            close
          </button>
        </div>

        <div className="dialog-body">
          <p className="q">{doc.question}</p>
          <p className="measures">{doc.measures}</p>

          {doc.components && (
            <>
              <h3>{doc.componentsLabel}</h3>
              <ul className="components">
                {doc.components.map((c) => (
                  <li key={c.name}>
                    <strong>{c.name}</strong>
                    <span>{c.text}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <h3>Scale</h3>
          <table className="wrap anchors">
            <thead>
              <tr>
                <th>Score</th>
                <th className="left">Level</th>
                {doc.anchors.some((a) => a.rung) && <th className="left">Rung</th>}
                <th className="left">Test</th>
              </tr>
            </thead>
            <tbody>
              {doc.anchors.map((a) => (
                <tr key={a.score}>
                  <td className="anchor">
                    <span className="num" style={scoreStyle(a.score)}>
                      {a.score}
                    </span>
                  </td>
                  <td className="left">
                    <strong>{a.level}</strong>
                  </td>
                  {doc.anchors.some((x) => x.rung) && (
                    <td className="left muted">{a.rung ?? ""}</td>
                  )}
                  <td className="left">{a.test}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="measures">
            Intermediate values (40, 70) mean “past this anchor, short of the next” and should
            never be used to avoid a judgement. Blank means not assessed, and counts as zero in
            the average: unassessed is unproven, and unproven earns no credit.
          </p>

          {doc.notes?.map((note) => (
            <p className="note" key={note.slice(0, 24)}>
              {note}
            </p>
          ))}

          <h3>The generic scale</h3>
          <table className="wrap anchors">
            <tbody>
              {GENERIC_SCALE.map((g) => (
                <tr key={g.score}>
                  <td className="anchor">
                    <span className="num" style={scoreStyle(g.score)}>
                      {g.score}
                    </span>
                  </td>
                  <td className="left">{g.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
